/**
 * Transfer engine client (BRD EV-17..EV-22, EV-30).
 *
 * Originator calls go out under the Originator identity; custodian calls under
 * the Custodian identity. Both share the backend error contract: `{message, code}`.
 */
import { authFetch } from '../auth/authFetch';
import { redirectToSignIn, requireIdentityHeaders } from '../auth/identity';
import { WORKSPACE_IDS } from '../auth/workspaces';
import { ApiError } from './apiError';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').replace(
  /\/$/,
  ''
);

/** Transaction types the engine accepts today. LOCATION is catalogued but deferred. */
export const TRANSACTION_TYPES = [
  { value: 'CUSTODY', label: 'Custody transfer', hint: 'Move safekeeping to another custodian' },
  { value: 'CONTROL', label: 'Control transfer', hint: 'Hand instruction rights to another party' },
  { value: 'ASSIGNMENT', label: 'Assignment', hint: 'Sell or assign ownership' },
  { value: 'PLEDGE', label: 'Pledge', hint: 'Grant a security interest to a pledgee' },
  { value: 'RELEASE', label: 'Release', hint: 'Lift an existing pledge' },
];

export const TRANSFER_STATES = [
  'INITIATED',
  'LOCKED',
  'ACCEPTED',
  'COMMITTED',
  'COMPLETED',
  'REJECTED',
  'CANCELLED',
  'EXCEPTION',
];

/** Open states the custodian still has to act on. */
export const TRANSFER_OPEN_STATES = ['INITIATED', 'LOCKED', 'ACCEPTED', 'EXCEPTION'];

const FACET_LABELS = {
  owner_org_id: 'Owner',
  owner_label: 'Owner',
  control_org_id: 'Control party',
  control_label: 'Control party',
  custodian_org_id: 'Custodian',
  custodian_label: 'Custodian',
  held_for_org_id: 'Held for',
  held_for_label: 'Held for',
  pledgee_org_id: 'Pledgee',
  pledgee_label: 'Pledgee',
  logical_account: 'Logical account',
  pledged: 'Pledged flag',
};

/** Human label for an authority facet key; de-duplicates the `_org_id` / `_label` pairs. */
export function facetLabel(key) {
  return FACET_LABELS[key] || String(key || '').replace(/_/g, ' ');
}

/** Distinct human labels for a list of facet keys, preserving order. */
export function facetLabels(keys) {
  const seen = new Set();
  const out = [];
  (keys || []).forEach((key) => {
    const label = facetLabel(key);
    if (!seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  });
  return out;
}

export function transactionTypeLabel(value) {
  const found = TRANSACTION_TYPES.find((type) => type.value === value);
  return found ? found.label : value || '—';
}

function buildQueryString(query) {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

function extractErrorMessage(payload, status) {
  const fallback = `Request failed (${status})`;
  if (!payload || typeof payload !== 'object') return fallback;
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
  if (typeof payload.detail === 'string' && payload.detail.trim()) return payload.detail;
  return fallback;
}

async function parseResponse(response) {
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    let payload = null;
    try {
      payload = await response.json();
      message = extractErrorMessage(payload, response.status);
    } catch {
      // Empty or non-JSON body: keep the status fallback.
    }
    const code =
      payload && typeof payload === 'object' && typeof payload.code === 'string' ? payload.code : null;
    throw new ApiError(message, { status: response.status, code, payload });
  }
  if (response.status === 204) return null;
  return response.json();
}

async function request(workspace, path, { method = 'GET', body, query, headers } = {}) {
  const response = await authFetch(`${API_BASE_URL}${path}${buildQueryString(query)}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...requireIdentityHeaders(workspace),
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse(response);
}

const originator = (path, options) => request(WORKSPACE_IDS.ORIGINATOR, path, options);
const custodian = (path, options) => request(WORKSPACE_IDS.CUSTODIAN, path, options);

// --------------------------------------------------------------------------- //
// Originator (current control party)
// --------------------------------------------------------------------------- //

/** What each transaction type changes and what stays unchanged (BRD 9.2). */
export function listTransferTypes() {
  return originator('/v1/originator/transfers/types');
}

/**
 * Instruct a transfer. The backend validates, locks the authority version and
 * returns the LOCKED transaction. `idempotencyKey` is reused across retries.
 */
export function initiateTransfer(assetId, payload, idempotencyKey) {
  return originator(`/v1/originator/assets/${assetId}/transfers`, {
    method: 'POST',
    body: {
      transaction_type: payload.transaction_type,
      counterparty_org_id: payload.counterparty_org_id ?? null,
      counterparty_label: payload.counterparty_label ?? null,
      changes: payload.changes ?? {},
      notes: payload.notes ?? null,
      effective_at: payload.effective_at ?? null,
    },
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

export function listAssetTransfers(assetId) {
  return originator(`/v1/originator/assets/${assetId}/transfers`);
}

export function cancelTransfer(transactionId, reason) {
  return originator(`/v1/originator/transfers/${transactionId}/cancel`, {
    method: 'POST',
    body: { reason: reason ?? null },
  });
}

// --------------------------------------------------------------------------- //
// Custodian (accepts under the programme mandate, commits the authority change)
// --------------------------------------------------------------------------- //

export function listCustodianTransfers({
  page = 1,
  pageSize = 50,
  states,
  transactionType,
} = {}) {
  return custodian('/v1/custodian/transfers', {
    query: {
      page,
      page_size: pageSize,
      state: Array.isArray(states) ? states.join(',') : states,
      transaction_type: transactionType,
    },
  });
}

export function getCustodianTransfer(transactionId) {
  return custodian(`/v1/custodian/transfers/${transactionId}`);
}

export function acceptTransfer(transactionId, reason) {
  return custodian(`/v1/custodian/transfers/${transactionId}/accept`, {
    method: 'POST',
    body: { reason: reason ?? null },
  });
}

export function rejectTransfer(transactionId, reason) {
  return custodian(`/v1/custodian/transfers/${transactionId}/reject`, {
    method: 'POST',
    body: { reason: reason ?? null },
  });
}

export function commitTransfer(transactionId) {
  return custodian(`/v1/custodian/transfers/${transactionId}/commit`, {
    method: 'POST',
  });
}

export function markTransferException(transactionId, reason) {
  return custodian(`/v1/custodian/transfers/${transactionId}/exception`, {
    method: 'POST',
    body: { reason: reason ?? null },
  });
}

export function getTransferReceipt(transactionId) {
  return custodian(`/v1/custodian/transfers/${transactionId}/receipt`);
}
